import { SelectOption } from '@chronosphereio/chrono-ui';
import constate from 'constate';
import { useCallback, useState } from 'react';
import { useImmer } from 'use-immer';
import { PROMETHEUS_REDUCER_OPTIONS, PrometheusQuerySpec, SeriesNameType } from '@/core';
import { OptionsEditorProps } from '@/plugin-system';

export type PrometheusTimeSeriesQueryEditorProps = OptionsEditorProps<PrometheusQuerySpec> & {
  showVariableSuggestion?: boolean;
  showSeriesNameFormat?: boolean;
  showMinStep?: boolean;
  onRun?: () => void;
};

export const SERIES_NAME_TYPE_DEFAULT = SeriesNameType.Labels;
export const SERIES_NAME_TYPE_OPTIONS = [
  {
    key: SeriesNameType.Labels,
    label: 'Labels',
    description: 'Name series by choosing the labels to include.',
  },
  {
    key: SeriesNameType.Regex,
    label: 'Regex',
    description: 'Name series by defining a regex and choosing the capture groups to include.',
  },
];

export const REDUCER_OPTIONS: SelectOption[] = Object.entries(PROMETHEUS_REDUCER_OPTIONS).map(([key, options]) => {
  return {
    key,
    ...options,
  };
});

function useEditorStateInternal(props: PrometheusTimeSeriesQueryEditorProps) {
  const { onChange, value, dataType } = props;

  const [lastSyncedSpec, setLastSyncedSpec] = useState(value);
  const [spec, setSpec] = useImmer(value);

  const triggerOnChange = useCallback(() => {
    onChange(spec);
  }, [spec, onChange]);

  // Used by PromQLBuilder. Can be backfilled to PromQLEditor.
  const handleChange = useCallback(
    (query: string, options?: { shouldRun?: boolean; shouldTriggerOnChange?: boolean }) => {
      setSpec((draft) => {
        draft.query = query;
      });

      if (options?.shouldRun) {
        onChange({ ...spec, query }, { shouldRunQuery: true });
      } else if (options?.shouldTriggerOnChange) {
        onChange({ ...spec, query });
      }
    },
    [spec, setSpec, onChange]
  );

  if (props.value !== lastSyncedSpec) {
    setSpec(props.value);
    setLastSyncedSpec(props.value);
  }

  return {
    handleChange,
    triggerOnChange,
    spec,
    setSpec,
    dataType,
  };
}

export const [EditorProvider, useEditorState] = constate(useEditorStateInternal);
